// src/App.tsx
/**
 * App routes with auth gate + role guards
 * -----------------------------------------------------------------------------
 * - Public: /login, /unauthorized
 * - Private: everything else wrapped in <RequireAuth/>
 * - Keeps existing RouteGuard role restrictions for sensitive pages
 *
 * After login, users are redirected to the originally requested route (?next=)
 * or "/" by default.
 */

import React from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";

// Pages
import Dashboard from "@/pages/Dashboard";
import LoadsIndex from "@/pages/Loads/LoadsIndex";
import PaperEntry from "@/pages/Loads/PaperEntry";
import ImportMillData from "@/pages/Loads/ImportMillData";
import ImportTemplates from "@/pages/Loads/ImportTemplates";
import LoadsInbox from "@/pages/Loads/LoadsInbox";
import ManifestsIndex from "@/pages/Loads/ManifestsIndex";
import ManifestDetail from "@/pages/Loads/ManifestDetail";
import LoadDetail from "@/pages/Loads/LoadDetail";
import SitesIndex from "@/pages/Sites/SitesIndex";
import SiteDetail from "@/pages/Sites/SiteDetail";
import ProductsIndex from "@/pages/Products/ProductsIndex";
import ReportsIndex from "@/pages/Reports/ReportsIndex";
import JobsIndex from "@/pages/Jobs/JobsIndex";
import RateHistory from "@/pages/Jobs/RateHistory";
import MillsIndex from "@/pages/Mills/MillsIndex";
import ContactsIndex from "@/pages/Contacts/ContactsIndex";
import CustomersIndex from "@/pages/Customers/CustomersIndex";
import Weighbridge from "@/pages/Admin/Weighbridge";
import AuditLog from "@/pages/Admin/AuditLog";
import SettlementBuilder from "@/pages/Settlements/Builder";
import SettlementPrintView from "@/pages/Settlements/PrintView";
import Unauthorized from "@/pages/Unauthorized";
import JobForm from "@/pages/Jobs/JobForm";
import LoginLanding from "@/pages/Auth/LoginLanding";

// Guards
import RouteGuard from "@/components/RouteGuard";

// Storage (for auth session)
import { get } from "@/lib/storage";

const SESSION_KEY = "auth:session:v1";

/** Local auth wrapper so we don't need a separate file. */
function RequireAuth() {
  const loc = useLocation();
  const s = get<any>(SESSION_KEY, null);
  const authed = !!(s && s.user && s.role);
  if (authed) return <Outlet />;

  const next = encodeURIComponent(loc.pathname + loc.search);
  return <Navigate to={`/login?next=${next}`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginLanding />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Private routes (require session) */}
      <Route element={<RequireAuth />}>
        <Route path="/" element={<Dashboard />} />

        {/* Loads */}
        <Route path="/loads" element={<LoadsIndex />} />
        <Route path="/loads/:id" element={<LoadDetail />} />
        <Route
          path="/loads/manifests"
          element={
            <RouteGuard roles={["Admin", "Dispatcher", "Accountant"]}>
              <ManifestsIndex />
            </RouteGuard>
          }
        />
        <Route
          path="/loads/manifest/:container"
          element={
            <RouteGuard roles={["Admin", "Dispatcher", "Accountant"]}>
              <ManifestDetail />
            </RouteGuard>
          }
        />
        <Route
          path="/loads/paper-entry"
          element={
            <RouteGuard roles={["Admin", "Dispatcher"]}>
              <PaperEntry />
            </RouteGuard>
          }
        />
        <Route
          path="/loads/import"
          element={
            <RouteGuard roles={["Admin", "Dispatcher", "Accountant"]}>
              <ImportMillData />
            </RouteGuard>
          }
        />
        <Route
          path="/loads/import-templates"
          element={
            <RouteGuard roles={["Admin", "Dispatcher", "Accountant"]}>
              <ImportTemplates />
            </RouteGuard>
          }
        />
        <Route
          path="/loads/inbox"
          element={
            <RouteGuard roles={["Admin", "Dispatcher"]}>
              <LoadsInbox />
            </RouteGuard>
          }
        />

        {/* Sites */}
        <Route path="/sites" element={<SitesIndex />} />
        <Route path="/sites/:id" element={<SiteDetail />} />

        {/* Products / Reports / Jobs / Mills */}
        <Route path="/products" element={<ProductsIndex />} />
        <Route path="/reports" element={<ReportsIndex />} />
        <Route path="/jobs" element={<JobsIndex />} />
        <Route path="/jobs/rate-history" element={<RateHistory />} />
        <Route path="/jobs/new" element={<JobForm mode="create" />} />
        <Route path="/jobs/:id" element={<JobForm mode="edit" />} />
        <Route path="/mills" element={<MillsIndex />} />

        {/* Contacts & Customers */}
        <Route path="/contacts" element={<ContactsIndex />} />
        {/* Legacy redirect for old links */}
        <Route path="/vendors" element={<Navigate to="/contacts" replace />} />
        <Route path="/customers" element={<CustomersIndex />} />

        {/* Admin */}
        <Route
          path="/admin/weighbridge"
          element={
            <RouteGuard roles={["Admin"]}>
              <Weighbridge />
            </RouteGuard>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <RouteGuard roles={["Admin", "Accountant"]}>
              <AuditLog />
            </RouteGuard>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RouteGuard roles={["Admin"]}>
              <Weighbridge />
            </RouteGuard>
          }
        />

        {/* Settlements */}
        <Route
          path="/settlements/builder"
          element={
            <RouteGuard roles={["Admin", "Accountant"]}>
              <SettlementBuilder />
            </RouteGuard>
          }
        />
        <Route path="/settlements/:id/print" element={<SettlementPrintView />} />

        {/* Catch-all (private space) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* Catch-all (public space) -> login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

